import { useEffect, useRef, useState, useCallback } from 'react'
import styled from '@emotion/styled'
import { getBodyTypeForFyusion } from '../../utils/vehicleUtils'
import { useGlobalState } from '../../hooks/GlobalStateProvider'
import useFyusionFetcher from '../../hooks/useFyusionFetcher'
import { colors } from '@argo/principles'
import useFyusionSessionSave from '../../hooks/useFyusionSessionSave'

const FyusionImageCapture = ({ vehicleBodyStyle, vin, dealerGuid }) => {
  const containerRef = useRef(null)
  const widgetRef = useRef(null)
  const sessionIdRef = useRef(null)
  const isSessionSavedRef = useRef(false)
  const sessionSavePromiseRef = useRef(null)
  const [scriptLoaded, setScriptLoaded] = useState(false)
  const [widgetInitialized, setWidgetInitialized] = useState(false)

  const fyusionBodyType = getBodyTypeForFyusion(vehicleBodyStyle)
  const { globalState } = useGlobalState()
  const env = globalState.environment

  // Fetch OAuth2 token for Fyusion API
  const { accessToken, error: tokenError, isLoading: isTokenLoading } = useFyusionFetcher()
  const submitSession = useFyusionSessionSave(vin, dealerGuid)

  const resetWidgetState = () => {
    widgetRef.current = null
    sessionIdRef.current = null
    isSessionSavedRef.current = false
    sessionSavePromiseRef.current = null
    setWidgetInitialized(false)
  }

  // Load the Fyusion Camera Widget script when component mounts
  useEffect(() => {
    if (!window.CameraWidget && !scriptLoaded) {
      if (!env.fyusionScriptUrl) {
        console.error('Fyusion script URL is not configured.')
        return
      }

      const script = document.createElement('script')
      script.src = env.fyusionScriptUrl
      script.onload = () => {
        setScriptLoaded(true)
      }
      script.onerror = () => {
        console.error('Failed to load Fyusion Camera Widget script.')
      }
      document.head.appendChild(script)
    } else if (window.CameraWidget && !scriptLoaded) {
      setScriptLoaded(true)
    }

    // Cleanup function (on unmount - for navigation within app)
    return () => {
      if (widgetRef.current) {
        if (typeof widgetRef.current.destroy === 'function') {
          widgetRef.current.destroy()
        }
        resetWidgetState()
      }
    }
  }, [env.fyusionScriptUrl, scriptLoaded])

  const initializeWidget = useCallback(() => {
    if (containerRef.current && !widgetRef.current && scriptLoaded && window.CameraWidget && accessToken) {
      setWidgetInitialized(true)

      widgetRef.current = window.CameraWidget.init({
        targetId: containerRef.current.id,
        apiBaseUrl: env.fyusionApiUrl,
        accountId: env.fyusionAccountId,
        bodyType: fyusionBodyType, // Options: "sedan", "suv", "pickup", "minivan"
        maxPhotos: 14,
        assetPath: env.fyusionAssetsUrl,
        cssPrefix: 'fyusion-widget',
        accessToken: accessToken, // OAuth2 access token from Edge API

        onPhotoCapture: async () => {
          // Capture session ID if not already set
          let currentSessionId = sessionIdRef.current
          if (!currentSessionId && widgetRef.current && typeof widgetRef.current.getSessionId === 'function') {
            currentSessionId = widgetRef.current.getSessionId()
            if (currentSessionId) {
              sessionIdRef.current = currentSessionId
            }
          }

          if (currentSessionId && !isSessionSavedRef.current && !sessionSavePromiseRef.current) {
            // Set flag immediately to prevent multiple concurrent saves
            isSessionSavedRef.current = true

            // Store the promise so onComplete can wait for it
            sessionSavePromiseRef.current = submitSession(currentSessionId)

            try {
              const result = await sessionSavePromiseRef.current
              // If save failed, reset flag so it can be retried
              if (result?.error) {
                isSessionSavedRef.current = false
              }
            } catch (error) {
              console.error('Session save error:', error)
              isSessionSavedRef.current = false
            } finally {
              sessionSavePromiseRef.current = null
            }
          }
        },

        onComplete: async () => {
          // Wait for any pending session save to complete before destroying widget
          if (sessionSavePromiseRef.current) {
            try {
              await sessionSavePromiseRef.current
            } catch (error) {
              console.error('Error waiting for session save on complete:', error)
            }
          }

          // Destroy widget after completion
          if (widgetRef.current && typeof widgetRef.current.destroy === 'function') {
            widgetRef.current.destroy()
            resetWidgetState()
          }
        },

        onError: (error, message) => {
          console.error('Fyusion Widget Error:', error, message)
        }
      })

      if (widgetRef.current) {
        if (typeof widgetRef.current.initializeSession === 'function') {
          widgetRef.current.initializeSession()
        }

        if (typeof widgetRef.current.enterFullscreenMode === 'function') {
          widgetRef.current.enterFullscreenMode()
        }
      }
    }
  }, [vehicleBodyStyle, scriptLoaded, accessToken])

  const handleStartCapture = useCallback(() => {
    if (widgetInitialized && widgetRef.current && typeof widgetRef.current.enterFullscreenMode === 'function') {
      widgetRef.current.enterFullscreenMode()
    } else {
      initializeWidget()
    }
  }, [widgetInitialized, initializeWidget])

  return (
    <ContentWrapper className="fyusion-image-capture" data-testid="fyusion-image-capture-wrapper">
      <ContentBox data-testid="fyusion-content-box">
        <Title data-testid="fyusion-title">
          <BetaBadge data-testid="fyusion-beta-badge">BETA</BetaBadge> | Damage Detection
        </Title>
        <p data-testid="fyusion-description">Capture photos on your mobile device, and we'll analyze them for damage.</p>
        {tokenError ? (
          <ErrorMessage data-testid="fyusion-error">Failed to authenticate with Fyusion service. Please try again.</ErrorMessage>
        ) : (
          <CaptureButton onClick={handleStartCapture} disabled={!scriptLoaded || !accessToken || isTokenLoading} data-testid="fyusion-btn-take-photos">
            {isTokenLoading ? 'Loading...' : 'Take Photos'}
          </CaptureButton>
        )}
      </ContentBox>
      <WidgetContainer ref={containerRef} id="fyusion-camera-widget" $initialized={widgetInitialized} data-testid="fyusion-widget-container" />
    </ContentWrapper>
  )
}

const ContentWrapper = styled.div`
  width: 100%;
`

const ContentBox = styled.div`
  background: #e6f5fe;
  padding: 20px 10px;
  margin-bottom: 10px;
  border-radius: 4px;
  text-align: left;
  p {
    font-size: 14px;
  }
`

const Title = styled.h2`
  font-size: 14px;
  margin-bottom: 10px;
`

const BetaBadge = styled.span`
  font-size: 12px;
  font-weight: normal;
  background: #4a4a4a;
  color: white;
  padding: 2px 8px;
  border-radius: 20px;
  margin-right: 3px;
`

const ErrorMessage = styled.p`
  color: #d32f2f;
  font-size: 14px;
  margin: 10px 0;
  font-weight: 500;
`

const CaptureButton = styled.button`
  background: ${colors.action.darkBrightBlue};
  color: white;
  border: none;
  width: 100%;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`

const WidgetContainer = styled.div`
  height: ${props => (props.$initialized ? '500px' : '0px')};
  width: 100%;
  overflow: hidden;
`

export default FyusionImageCapture
